import { useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import moment from 'moment';
import { Checkbox, DatePicker } from 'antd';
import Card from '../../common/components/Card';
import Button from '../../common/components/Button';
import WipReportGrid from './WipReportGrid';
import { exportWipExcel } from './wipReportExcel';

const GET_WIP_REPORT = gql`
  query getWipReport($month: String!, $includeAllStatuses: Boolean, $includeArchived: Boolean) {
    finance {
      wipReport(
        month: $month
        includeAllStatuses: $includeAllStatuses
        includeArchived: $includeArchived
      ) {
        _id
        name
        projectCode
        clientName
        budget
        totalApprovedHours
        monthToDateHours
        budgetedHours
        actualHoursRemainingOverride
        estimatedEndDate
        totalBilledAmount
        owner
        status
      }
    }
  }
`;

// Percent-complete revenue recognition.
// Actual Hours Remaining defaults to budgeted - approved hours; a per-project
// manual override (actualHoursRemainingOverride) wins when set. When the inputs
// needed for percent complete are missing, the derived columns stay blank
// (matching the source spreadsheet's #N/A) rather than fabricating zeros.
const deriveRow = row => {
  const worked = row.totalApprovedHours;
  const contract = row.budget;
  const billed = row.totalBilledAmount;

  // Default from deliverable budgeted hours; clamp at 0 (over-budget => none left).
  // Budgeted hours of 0 (or unset) means no basis to recognize: leave it blank
  // rather than treating the project as 100% complete.
  const derivedRemaining =
    typeof row.budgetedHours === 'number' && row.budgetedHours > 0 && typeof worked === 'number'
      ? Math.max(0, row.budgetedHours - worked)
      : null;
  const remaining =
    typeof row.actualHoursRemainingOverride === 'number'
      ? row.actualHoursRemainingOverride
      : derivedRemaining;
  const remainingSource =
    typeof row.actualHoursRemainingOverride === 'number' ? 'override' : 'derived';

  const totalHours =
    typeof worked === 'number' && typeof remaining === 'number' ? worked + remaining : null;
  const percentComplete = totalHours > 0 ? worked / totalHours : null;
  const incomeRecognized =
    percentComplete !== null && typeof contract === 'number' ? contract * percentComplete : null;
  const backlog = incomeRecognized !== null ? contract - incomeRecognized : null;
  const overUnderBilled =
    incomeRecognized !== null && typeof billed === 'number' ? billed - incomeRecognized : null;

  return {
    ...row,
    actualHoursRemaining: remaining,
    remainingSource,
    totalHours,
    percentComplete,
    incomeRecognized,
    backlog,
    overUnderBilled,
  };
};

const WipReport = () => {
  const [month, setMonth] = useState(moment().format('YYYY-MM'));
  // Off by default: the report lists open projects plus anything worked in the month.
  const [includeAllStatuses, setIncludeAllStatuses] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const { loading, data } = useQuery(GET_WIP_REPORT, {
    variables: { month, includeAllStatuses, includeArchived },
  });

  const rows = (data?.finance?.wipReport || []).map(deriveRow);

  return (
    <Card
      loading={loading}
      border
      title={`Work in Progress Report — ${moment(month, 'YYYY-MM').format('MMMM YYYY')}`}
      floating
      padded={false}
      actionComponent={
        <div className="flex items-center gap-2">
          <Checkbox
            checked={includeAllStatuses}
            onChange={e => setIncludeAllStatuses(e.target.checked)}
          >
            Include all statuses
          </Checkbox>
          <Checkbox checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)}>
            Include archived
          </Checkbox>
          <DatePicker.MonthPicker
            allowClear={false}
            format="MMMM YYYY"
            value={moment(month, 'YYYY-MM')}
            onChange={m => m && setMonth(m.format('YYYY-MM'))}
          />
          <Button onClick={() => exportWipExcel(rows, month)}>Export</Button>
        </div>
      }
    >
      <WipReportGrid rowData={rows} />
    </Card>
  );
};

export default WipReport;
